import { Link } from "react-router-dom";
import { MapPin, Car } from "lucide-react";
import Seo from "@/components/Seo";
import SectionReveal from "@/components/SectionReveal";
import LeadForm from "@/components/LeadForm";
import { LandingHero, localBusinessJsonLd, ADDRESS } from "@/components/LandingBits";
import { trackCta } from "@/lib/analytics";

const PATH = "/studio-hire-windermere";

const USES = [
  "Pilates & yoga instructors", "Fitness coaches & personal trainers", "Therapists, massage & physio professionals",
  "Workshops & education sessions", "Community groups & clubs", "Small organisations, coaches & consultants",
];

const SPACES = [
  { img: "/gallery/studio-hire-1.webp", alt: "Upstairs studio at KORE Wellbeing, Rowan House — open carpeted space with oak doors and natural light", t: "Upstairs studio", d: "A calm, naturally lit studio space above the gym floor — suited to classes, movement sessions, therapy and small group work. Contact us to discuss layout, capacity and availability." },
  { img: "/gallery/gym-floor.webp", alt: "KORE Wellbeing gym floor available for coaching hire in Windermere", t: "Gym floor", d: "A fully equipped training floor for coaches and instructors who need strength and conditioning equipment for their own clients. Contact us to discuss your requirements." },
];

const STUDIO_GALLERY = [
  ["/gallery/studio-hire-2.webp", "Upstairs studio at KORE Wellbeing — carpeted open-plan room with oak-framed doors"],
  ["/gallery/studio-hire-3.webp", "Upstairs studio at KORE Wellbeing looking towards the kitchenette area"],
  ["/gallery/studio-upstairs.webp", "Upstairs studio at KORE Wellbeing with mats laid out"],
];

const FACILITIES = [
  "Kitchenette", "Natural light", "Oak doors", "Window blinds", "Carpeted floor", "Ceiling lighting", "Radiators / heating",
];

export default function StudioHire() {
  return (
    <div data-testid="studio-hire-page">
      <Seo
        title="Studio & Room Hire Windermere"
        description="Flexible studio and room hire in Bowness-on-Windermere for instructors, coaches, therapists, workshops, clubs and local organisations."
        path={PATH}
        jsonLd={localBusinessJsonLd({ url: `https://korewellbeing.com${PATH}`, description: "Studio and space hire at KORE Wellbeing, Rowan House, Bowness-on-Windermere." })}
      />

      <LandingHero
        testId="hire-hero"
        eyebrow="Space Hire · KORE Wellbeing · Bowness-on-Windermere"
        h1="Studio & Space Hire in Windermere"
        sub="Professional, flexible space in the heart of Bowness-on-Windermere for classes, coaching, therapy, workshops and community use."
        image="/gallery/studio-hire-1.webp"
        imageAlt="Upstairs studio available to hire at KORE Wellbeing, Bowness-on-Windermere"
        primary={{ to: "#enquire", label: "Request availability & rates", onClick: () => trackCta({ cta_name: "hire_enquire", cta_text: "Request availability & rates", destination_url: "#enquire", cta_location: "hero" }) }}
      />

      <SectionReveal as="section" data-testid="hire-intro" className="bg-kore-warm py-24 md:py-32">
        <div className="container-kore grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <span className="eyebrow">Part of the KORE Wellbeing club</span>
            <h2 className="display-2 mt-4 text-kore-forest">Good space, <span className="italic text-kore-gold">well looked after.</span></h2>
            <div className="mt-8 space-y-5 text-kore-ink/80 leading-relaxed text-[17px] max-w-xl">
              <p>KORE Wellbeing is an independent health club at Rowan House in Bowness-on-Windermere. Alongside our own coaching and gym membership, our spaces are available to hire by local instructors, therapists, groups and organisations.</p>
              <p>Hire is flexible — hourly, by session or on a longer-term basis — and we'll work around what you need. Where details aren't listed here, contact us to discuss your requirements.</p>
            </div>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <div className="eyebrow">Potential uses</div>
            <ul className="mt-6 space-y-3">
              {USES.map((u) => <li key={u} className="font-display text-xl text-kore-forest border-b border-kore-gold/20 pb-3">{u}</li>)}
            </ul>
          </div>
        </div>
      </SectionReveal>

      <SectionReveal as="section" data-testid="hire-spaces" className="bg-kore-warm pb-12 md:pb-16">
        <div className="container-kore grid md:grid-cols-2 gap-8 lg:gap-12">
          {SPACES.map((s) => (
            <figure key={s.t} data-testid={`hire-space-${s.t.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="rounded-3xl overflow-hidden aspect-[4/3] shadow-soft"><img src={s.img} alt={s.alt} loading="lazy" className="w-full h-full object-cover" /></div>
              <figcaption className="mt-6"><h3 className="font-display text-2xl md:text-3xl text-kore-forest">{s.t}</h3><p className="mt-3 text-kore-ink/75 leading-relaxed">{s.d}</p></figcaption>
            </figure>
          ))}
        </div>
      </SectionReveal>

      <section data-testid="hire-studio-gallery" className="bg-kore-warm pb-16 md:pb-20">
        <div className="container-kore grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {STUDIO_GALLERY.map(([src, alt]) => (
            <div key={src} className="rounded-3xl overflow-hidden aspect-[3/4] shadow-soft"><img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" /></div>
          ))}
        </div>
      </section>

      <SectionReveal as="section" data-testid="hire-included" className="bg-kore-warm pb-24 md:pb-32">
        <div className="container-kore grid md:grid-cols-12 gap-10 border-t border-kore-gold/20 pt-12">
          <div className="md:col-span-4">
            <span className="eyebrow">What's included</span>
            <h2 className="font-display text-3xl md:text-4xl text-kore-forest mt-4 leading-tight">The upstairs studio.</h2>
            <p className="mt-4 text-kore-ink/70 leading-relaxed text-sm">Anything not listed here — capacity, equipment, access times — contact us to discuss your requirements.</p>
          </div>
          <ul className="md:col-span-7 md:col-start-6 grid sm:grid-cols-2 gap-x-8 gap-y-4 text-[17px] text-kore-forest">
            {FACILITIES.map((f) => <li key={f} className="border-b border-kore-gold/20 pb-3">{f}</li>)}
          </ul>
        </div>
      </SectionReveal>

      <SectionReveal as="section" data-testid="hire-location" className="bg-kore-warm pb-24 md:pb-32">
        <div className="container-kore grid sm:grid-cols-2 gap-8 border-t border-kore-gold/20 pt-12 max-w-4xl">
          <div className="flex gap-3"><MapPin size={18} className="text-kore-gold mt-1 shrink-0" /><div><div className="eyebrow">Location</div><p className="mt-3 text-kore-ink/80 leading-relaxed">{ADDRESS.name}<br />{ADDRESS.street}<br />{ADDRESS.locality}, {ADDRESS.region} {ADDRESS.postcode}</p></div></div>
          <div className="flex gap-3"><Car size={18} className="text-kore-gold mt-1 shrink-0" /><div><div className="eyebrow">Parking & access</div><p className="mt-3 text-kore-ink/80 leading-relaxed">Parking available at Rowan House, a couple of minutes from the lake and Bowness town centre. Contact us to discuss access times.</p></div></div>
        </div>
      </SectionReveal>

      <section id="enquire" data-testid="hire-enquiry" className="bg-kore-forest text-kore-warm py-28 md:py-36 scroll-mt-20">
        <div className="container-kore grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <span className="eyebrow text-kore-light">Enquire</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl mt-5 leading-[1.05]">Tell us what you <span className="italic text-kore-light">have in mind.</span></h2>
            <p className="mt-6 text-kore-warm/75 leading-relaxed">We'll come back to you to talk through space, timings and what's possible. No obligation.</p>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-3xl bg-kore-warm text-kore-ink p-8 md:p-10">
              <LeadForm funnel="studio" submitLabel="Request availability & rates" testId="hire-form" showGoal={false}
                extraFields={[
                  { key: "organisation", label: "Business / organisation" },
                  { key: "practice_type", label: "Practice type", options: ["Physiotherapy", "Pilates", "Yoga", "1:1 personal training", "Massage / therapy", "Workshop / education", "Community group / club", "Other"], required: true },
                  { key: "preferred_times", label: "Preferred days and times" },
                  { key: "frequency", label: "How often would you need the space?" },
                  { key: "message", label: "What would you like to use the space for?", type: "textarea", required: true, wide: true },
                ]} />
            </div>
          </div>
        </div>
      </section>

      <SectionReveal as="section" data-testid="hire-back" className="bg-kore-warm py-16">
        <div className="container-kore text-center">
          <p className="text-kore-ink/70">Space hire is part of the wider <Link to="/" data-testid="hire-back-home" className="text-kore-forest border-b border-kore-gold pb-0.5">KORE Wellbeing</Link> club — a coaching-led gym for people 40+ in Bowness-on-Windermere.</p>
        </div>
      </SectionReveal>
    </div>
  );
}
